/**
 * WallpaperBackground Component
 *
 * Renders a dynamic wallpaper background with crossfade transitions
 * for the Mac Studio theme.
 */

import { useEffect, useRef, useMemo } from 'react'
import { useWallpaper } from '@/hooks/useWallpaper'
import { WALLPAPERS_DARK, type WallpaperConfig } from '@/types/wallpaper'

interface WallpaperBackgroundProps {
  /** Current scroll-based hue shift (0-360) for dynamic gradient */
  scrollHue?: number
  /** Whether dark mode is enabled */
  isDark?: boolean
  /** Children to render on top of wallpaper */
  children?: React.ReactNode
  /** Custom class for the container */
  className?: string
}

export function WallpaperBackground({
  scrollHue = 0,
  isDark = false,
  children,
  className = '',
}: WallpaperBackgroundProps) {
  const {
    currentWallpaper,
    isTransitioning,
    preloadedImageUrl,
    nextWallpaper,
  } = useWallpaper()

  const containerRef = useRef<HTMLDivElement>(null)

  // Update CSS variable for scroll-based hue shift
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.style.setProperty('--scroll-hue', String(scrollHue))
    }
  }, [scrollHue])

  // Get the appropriate gradient for current wallpaper
  const getWallpaperStyle = (
    wallpaper: WallpaperConfig,
    forDark: boolean
  ): React.CSSProperties => {
    if (wallpaper.type === 'image' && wallpaper.src) {
      return {
        backgroundImage: `url(${wallpaper.src})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    }

    if (wallpaper.type === 'gradient') {
      // Use dark variant if available and dark mode is enabled
      const gradient =
        forDark && WALLPAPERS_DARK[wallpaper.id]
          ? WALLPAPERS_DARK[wallpaper.id]
          : wallpaper.gradient

      return {
        background: gradient,
      }
    }

    return {}
  }

  // Memoize styles to prevent unnecessary recalculations
  const currentStyle = useMemo(
    () => getWallpaperStyle(currentWallpaper, isDark),
    [currentWallpaper, isDark]
  )

  const nextStyle = useMemo(
    () => (nextWallpaper ? getWallpaperStyle(nextWallpaper, isDark) : {}),
    [nextWallpaper, isDark]
  )

  // Check for reduced motion preference
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches

  return (
    <div
      ref={containerRef}
      className={`wallpaper-container ${className}`}
      style={
        {
          '--scroll-hue': scrollHue,
        } as React.CSSProperties
      }
    >
      {/* Current wallpaper layer */}
      <div
        className={`wallpaper-layer wallpaper-layer--current ${
          isTransitioning && !prefersReducedMotion ? 'wallpaper-layer--fading' : ''
        }`}
        style={currentStyle}
        aria-hidden="true"
      />

      {/* Next wallpaper layer (for crossfade) */}
      {isTransitioning && nextWallpaper && (
        <div
          className={`wallpaper-layer wallpaper-layer--next ${
            !prefersReducedMotion ? 'wallpaper-layer--appearing' : ''
          }`}
          style={nextStyle}
          aria-hidden="true"
        />
      )}

      {/* Preload next image (hidden) */}
      {preloadedImageUrl && (
        <link rel="preload" as="image" href={preloadedImageUrl} />
      )}

      {/* Content layer */}
      <div className="wallpaper-content">{children}</div>
    </div>
  )
}

export default WallpaperBackground
